import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { brandOptions } from '../../../constants/brands';
import { supabaseHelpers } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { settingsStore } from '../../../lib/localSettingsStore';
import { useToast } from '../../../contexts/ToastContext';

const EditFilamentDrawer = ({ isOpen, onClose, onSave, filament }) => {
  const { user } = useAuth();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const uploadTimersRef = useRef({});
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState(() => ({
    id: filament?.id,
    brand: filament?.brand || '',
    material: filament?.material || 'PLA',
    colorName: filament?.colorName || '',
    colorHex: filament?.colorHex || '#ffffff',
    originalWeight: filament?.originalWeight || '',
    currentWeight: filament?.currentWeight || '',
    totalCost: filament?.totalCost || '',
    purchaseDate: filament?.purchaseDate || new Date()?.toISOString()?.split('T')?.[0],
    diameter: filament?.diameter || '1.75',
    density: Number.isFinite(parseFloat(filament?.density)) ? parseFloat(filament?.density) : 1.24,
    gramsPerMeter: Number.isFinite(parseFloat(filament?.gramsPerMeter ?? filament?.grams_per_meter)) ? parseFloat(filament?.gramsPerMeter ?? filament?.grams_per_meter) : '',
    supplier: filament?.supplier || '',
    image_urls: Array.isArray(filament?.image_urls) ? filament?.image_urls : (filament?.image ? [filament?.image] : []),
    notes: filament?.notes || ''
  }));

  const [errors, setErrors] = useState({});

  // Marcas comuns para seleção rápida
  const commonBrandOptions = brandOptions;
  const initialBrandMode = commonBrandOptions.some(opt => opt.value === (filament?.brand || '')) ? 'select' : 'custom';
  const [brandMode, setBrandMode] = useState(initialBrandMode);

  const materialOptions = [
    { value: 'PLA', label: 'PLA (Ácido Polilático)' },
    { value: 'ABS', label: 'ABS (Acrilonitrila Butadieno Estireno)' },
    { value: 'PETG', label: 'PETG (Politereftalato de Etileno Glicol)' },
    { value: 'TPU', label: 'TPU (Poliuretano Termoplástico)' },
    { value: 'ASA', label: 'ASA (Acrilonitrila Estireno Acrilato)' },
    { value: 'WOOD', label: 'Wood Fill (Preenchimento de Madeira)' },
    { value: 'CARBON_FIBER', label: 'Carbon Fiber (Fibra de Carbono)' },
    { value: 'METAL_FILLED', label: 'Metal Filled (Preenchimento Metálico)' },
    { value: 'PC', label: 'PC (Policarbonato)' },
    { value: 'Nylon', label: 'Nylon' },
    { value: 'HIPS', label: 'HIPS (Poliestireno de Alto Impacto)' },
    { value: 'Other', label: 'Outro Material' }
  ];

  const diameterOptions = [
    { value: '1.75', label: '1.75mm (Padrão)' },
    { value: '2.85', label: '2.85mm' }
  ];

  const predefinedColors = [
    { name: 'Preto', hex: '#1a1a1a' },
    { name: 'Branco', hex: '#f5f5f5' },
    { name: 'Cinza', hex: '#a3a3a3' },
    { name: 'Vermelho', hex: '#e11d48' },
    { name: 'Azul', hex: '#3b82f6' },
    { name: 'Verde', hex: '#22c55e' },
    { name: 'Amarelo', hex: '#f59e0b' },
    { name: 'Laranja', hex: '#f97316' },
    { name: 'Rosa', hex: '#ec4899' },
    { name: 'Roxo', hex: '#8b5cf6' }
  ];

  // Materiais globais (densidades)
  const [globalMaterials, setGlobalMaterials] = useState([]);
  useEffect(() => {
    let active = true;
    const loadMaterials = async () => {
      try {
        const { data } = await settingsStore.getMaterialTypes();
        if (active) setGlobalMaterials(Array.isArray(data) ? data : []);
      } catch {}
    };
    if (isOpen) loadMaterials();
    return () => { active = false; };
  }, [isOpen]);

  const materialDensityMap = useMemo(() => {
    const map = new Map();
    (globalMaterials || []).forEach(mt => {
      const name = String(mt?.name || '').trim();
      const d = Number.isFinite(parseFloat(mt?.density)) ? parseFloat(mt?.density) : undefined;
      if (name && d) map.set(name, d);
    });
    return map;
  }, [globalMaterials]);

  const computeGramatura = (density, diameterMm) => {
    const d = parseFloat(density);
    const dia = parseFloat(diameterMm);
    if (!Number.isFinite(d) || !Number.isFinite(dia) || d <= 0 || dia <= 0) return undefined;
    const radiusCm = (dia / 10) / 2;
    const areaCm2 = Math.PI * Math.pow(radiusCm, 2);
    const volumeCm3PerMeter = areaCm2 * 100;
    const gramsPerMeter = d * volumeCm3PerMeter;
    return gramsPerMeter;
  };

  const handleInputChange = (field, value) => {
    let val = value;

    // Normalize gramsPerMeter to up to 3 decimals
    if (field === 'gramsPerMeter') {
      const raw = String(value || '')?.replace(',', '.').replace(/[^0-9.]/g, '');
      const parts = raw.split('.');
      val = parts.length > 1 ? `${parts[0]}.${parts.slice(1).join('').slice(0,3)}` : parts[0];
    }

    const next = { ...formData, [field]: val };

    // Atualizar densidade quando material muda (usando materiais globais)
    if (field === 'material') {
      const matName = val;
      const d = materialDensityMap.get(matName);
      if (Number.isFinite(d)) next.density = d;
    }

    // Auto-update gramsPerMeter when material/diameter/density changes, unless user set manual gramsPerMeter
    if (['material', 'diameter', 'density'].includes(field)) {
      const densityVal = parseFloat(field === 'density' ? val : (next?.density ?? filament?.density ?? 1.24));
      const diameterMm = parseFloat(field === 'diameter' ? val : (next?.diameter ?? filament?.diameter ?? '1.75'));
      const gpm = computeGramatura(densityVal, diameterMm);
      if (Number.isFinite(gpm) && (formData?.gramsPerMeter === '' || formData?.gramsPerMeter == null)) {
        next.gramsPerMeter = parseFloat(gpm.toFixed(3));
      }
    }

    setFormData(next);
  };

  const handleFilesUpload = async (files) => {
    if (!user?.id) return;
    if (!files || files?.length === 0) return;
    setUploadingImage(true);
    try {
      const remainingSlots = Math.max(0, 5 - (formData?.image_urls?.length || 0));
      const filesToUpload = Array.from(files)?.slice(0, remainingSlots);
      const uploadedUrls = [];
      for (const file of filesToUpload) {
        // Initialize progress for this file and start a simulated progress timer
        setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
        const timerId = setInterval(() => {
          setUploadProgress(prev => {
            const current = prev[file.name] ?? 0;
            const increment = Math.floor(Math.random() * 12) + 5;
            const next = Math.min(current + increment, 90);
            return { ...prev, [file.name]: next };
          });
        }, 200);
        uploadTimersRef.current[file.name] = timerId;
        const { publicUrl } = await supabaseHelpers?.uploadFilamentImage(file, user?.id);
        if (publicUrl) uploadedUrls.push(publicUrl);
        // Mark as complete and clear timer
        if (uploadTimersRef.current[file.name]) {
          clearInterval(uploadTimersRef.current[file.name]);
          delete uploadTimersRef.current[file.name];
        }
        setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
        setTimeout(() => {
          setUploadProgress(prev => {
            const { [file.name]: _, ...rest } = prev;
            return rest;
          });
        }, 1200);
      }
      setFormData(prev => ({ ...prev, image_urls: [...(prev?.image_urls || []), ...uploadedUrls] }));
    } catch (error) {
      console.error('Erro ao fazer upload das imagens:', error);
      toast.error('Erro ao fazer upload das imagens. Tente novamente.');
    } finally {
      setUploadingImage(false);
      Object.values(uploadTimersRef.current || {}).forEach(id => clearInterval(id));
      uploadTimersRef.current = {};
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.brand?.trim()) newErrors.brand = 'Marca é obrigatória';
    if (!formData?.colorName?.trim()) newErrors.colorName = 'Nome da cor é obrigatório';
    if (!formData?.originalWeight) newErrors.originalWeight = 'Peso original é obrigatório';
    if (!formData?.currentWeight) newErrors.currentWeight = 'Peso atual é obrigatório';
    if (!formData?.totalCost) newErrors.totalCost = 'Custo total é obrigatório';
    if (parseFloat(formData?.currentWeight) > parseFloat(formData?.originalWeight)) {
      newErrors.currentWeight = 'Peso atual não pode ser maior que o peso original';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      const resolvedDensity = Number.isFinite(parseFloat(formData?.density)) ? parseFloat(formData?.density) : (Number.isFinite(parseFloat(filament?.density)) ? parseFloat(filament?.density) : 1.24);
      const diameterMm = Number.isFinite(parseFloat(formData?.diameter)) ? parseFloat(formData?.diameter) : (Number.isFinite(parseFloat(filament?.diameter)) ? parseFloat(filament?.diameter) : 1.75);
      const gramsPerMeter = Number.isFinite(parseFloat(formData?.gramsPerMeter))
        ? parseFloat(formData?.gramsPerMeter)
        : computeGramatura(resolvedDensity, diameterMm);
      const updated = {
        ...filament,
        ...formData,
        image: (formData?.image_urls?.[0]) || filament?.image,
        image_urls: formData?.image_urls || [],
        costPerGram: parseFloat(formData?.totalCost) / parseFloat(formData?.originalWeight),
        density: resolvedDensity,
        grams_per_meter: Number.isFinite(gramsPerMeter) ? parseFloat(gramsPerMeter.toFixed(3)) : undefined
      };
      await onSave(updated);
      onClose();
    } catch (error) {
      console.error('Erro ao salvar alterações:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" onClick={onClose} />
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', stiffness: 260, damping: 30 }}
        className="fixed right-0 top-0 bottom-0 w-full max-w-xl bg-background border-l border-border z-50 overflow-y-auto"
      >
        <div className="p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Editar Filamento</h2>
          <p className="text-sm text-muted-foreground">Atualize as informações do filamento e gerencie a galeria de imagens.</p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Material Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <span className="w-2 h-2 bg-primary rounded-full"></span>
              <span>Informações do Material</span>
            </h3>
            {brandMode === 'select' ? (
              <div className="space-y-2">
                <Select
                  label="Marca"
                  options={commonBrandOptions}
                  value={formData?.brand}
                  onChange={(value) => handleInputChange('brand', value)}
                  searchable
                  clearable
                  required
                  description="Escolha uma marca comum."
                />
                <button
                  type="button"
                  className="text-xs text-primary hover:underline"
                  onClick={() => setBrandMode('custom')}
                >
                  Não encontrou? Adicionar nova marca
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <Input label="Marca" type="text" value={formData?.brand} onChange={(e) => handleInputChange('brand', e?.target?.value)} error={errors?.brand} required />
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:underline"
                  onClick={() => setBrandMode('select')}
                >
                  Preferir selecionar em uma lista
                </button>
              </div>
            )}
            <Select label="Tipo de Material" options={materialOptions} value={formData?.material} onChange={(value) => handleInputChange('material', value)} required />
            <Select label="Diâmetro do Filamento" options={diameterOptions} value={formData?.diameter} onChange={(value) => handleInputChange('diameter', value)} />
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Densidade (g/cm³)"
                type="number"
                step="0.01"
                placeholder="1.24"
                value={formData?.density}
                onChange={(e) => handleInputChange('density', e?.target?.value)}
                description="Usada para calcular a gramatura (g/m)"
              />
              <Input
                label="Gramatura (g/m)"
                type="number"
                step="0.001"
                placeholder="Calculado automaticamente"
                value={formData?.gramsPerMeter}
                onChange={(e) => handleInputChange('gramsPerMeter', e?.target?.value)}
                description="Opcional: sobrescreve o cálculo automático"
              />
            </div>
          </div>

          {/* Color */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <span className="w-2 h-2 bg-secondary rounded-full"></span>
              <span>Cor do Filamento</span>
            </h3>
            <div className="grid grid-cols-6 gap-2">
              {predefinedColors?.map((color) => (
                <button
                  key={color?.hex}
                  type="button"
                  onClick={() => {
                    setFormData(prev => ({ ...prev, colorHex: color?.hex, colorName: color?.name }));
                  }}
                  className={`w-8 h-8 rounded-full border-2 ${formData?.colorHex === color?.hex ? 'border-primary ring-2 ring-primary/20' : 'border-border'}`}
                  style={{ backgroundColor: color?.hex }}
                  title={color?.name}
                />
              ))}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input label="Nome da Cor" type="text" value={formData?.colorName} onChange={(e) => handleInputChange('colorName', e?.target?.value)} error={errors?.colorName} required />
              <Input label="Hex da Cor" type="text" value={formData?.colorHex} onChange={(e) => handleInputChange('colorHex', e?.target?.value)} />
            </div>
          </div>

          {/* Weights and Cost */}
          <div className="grid grid-cols-2 gap-4">
            <Input label="Peso Original (g)" type="number" value={formData?.originalWeight} onChange={(e) => handleInputChange('originalWeight', e?.target?.value)} error={errors?.originalWeight} required />
            <Input label="Peso Atual (g)" type="number" value={formData?.currentWeight} onChange={(e) => handleInputChange('currentWeight', e?.target?.value)} error={errors?.currentWeight} required />
            <Input label="Custo Total (R$)" type="number" step="0.01" value={formData?.totalCost} onChange={(e) => handleInputChange('totalCost', e?.target?.value)} error={errors?.totalCost} required />
            <Input label="Data de Compra" type="date" value={formData?.purchaseDate} onChange={(e) => handleInputChange('purchaseDate', e?.target?.value)} />
          </div>

          {/* Comprimento aproximado (preferindo valor manual de gramatura) */}
          {(() => {
            const manual = Number.isFinite(parseFloat(formData?.gramsPerMeter)) ? parseFloat(formData?.gramsPerMeter) : undefined;
            const densityVal = Number.isFinite(parseFloat(formData?.density)) ? parseFloat(formData?.density) : (parseFloat(filament?.density ?? 1.24));
            const diameterMm = parseFloat(formData?.diameter ?? filament?.diameter ?? '1.75');
            const gpm = Number.isFinite(manual) && manual > 0
              ? manual
              : (Number.isFinite(parseFloat(filament?.gramsPerMeter ?? filament?.grams_per_meter))
                ? parseFloat(filament?.gramsPerMeter ?? filament?.grams_per_meter)
                : computeGramatura(densityVal, diameterMm));
            if (!Number.isFinite(gpm) || gpm <= 0) return null;
            const origG = parseFloat(formData?.originalWeight || 0);
            const currG = parseFloat(formData?.currentWeight || 0);
            const metersOriginal = origG > 0 ? (origG / gpm) : null;
            const metersCurrent = currG > 0 ? (currG / gpm) : null;
            return (
              <div className="bg-muted rounded-lg p-3 border border-border">
                <div className="text-sm font-medium text-muted-foreground mb-1">Comprimento aproximado</div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Rolo</span>
                  <span className="text-sm text-foreground">{origG || 0}g{metersOriginal ? ` · ${metersOriginal.toFixed(1)} m` : ''}</span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-sm text-muted-foreground">Restante</span>
                  <span className="text-sm text-foreground">{currG || 0}g{metersCurrent ? ` · ${metersCurrent.toFixed(1)} m` : ''}</span>
                </div>
              </div>
            );
          })()}

          {/* Images */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <span className="w-2 h-2 bg-success rounded-full"></span>
              <span>Imagens do Filamento</span>
            </h3>
            <label className="block text-sm font-medium text-foreground mb-2">Selecionar imagens (até 5)</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => e?.target?.files && handleFilesUpload(e?.target?.files)}
              className="sr-only"
              disabled={uploadingImage}
            />
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                iconName="Upload"
                iconPosition="left"
                onClick={() => fileInputRef?.current?.click()}
                disabled={uploadingImage}
              >
                Selecionar imagens
              </Button>
              <span className="text-sm text-muted-foreground">
                {Array.isArray(formData?.image_urls) && formData?.image_urls?.length > 0
                  ? `${formData?.image_urls?.length} imagem(ns) selecionada(s)`
                  : 'Nenhuma imagem selecionada'}
              </span>
            </div>
            {uploadingImage && (<p className="text-sm text-primary mt-2">Enviando imagens...</p>)}
            {Object.keys(uploadProgress)?.length > 0 && (
              <div className="mt-3 space-y-2">
                {Object.entries(uploadProgress).map(([name, pct]) => (
                  <div key={name} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground truncate max-w-[60%]" title={name}>{name}</span>
                      <span className="text-xs text-foreground">{Math.round(pct)}%</span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                      <div className={`h-2 rounded-full ${pct < 100 ? 'bg-primary' : 'bg-success'}`} style={{ width: `${Math.max(3, Math.min(100, pct))}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {formData?.image_urls?.length > 0 && (
              <div className="grid grid-cols-3 gap-3">
                {formData?.image_urls?.map((url, idx) => (
                  <div key={idx} className="relative w-full h-24 rounded-lg overflow-hidden border border-border bg-muted">
                    <img src={url} alt={`Imagem ${idx + 1}`} className="w-full h-full object-cover" />
                    <button type="button" className="absolute top-2 right-2 px-2 py-1 text-xs bg-black/60 text-white rounded" onClick={() => setFormData(prev => ({ ...prev, image_urls: prev?.image_urls?.filter((_, i) => i !== idx) }))}>Remover</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Supplier and Notes */}
          <div className="grid grid-cols-1 gap-4">
            <Input label="Fornecedor" type="text" value={formData?.supplier} onChange={(e) => handleInputChange('supplier', e?.target?.value)} placeholder="Onde foi comprado (loja ou fornecedor)" />
            <Input label="Notas" type="textarea" rows={3} value={formData?.notes} onChange={(e) => handleInputChange('notes', e?.target?.value)} placeholder="Configurações, observações, etc." />
          </div>

          {/* Actions */}
          <div className="sticky bottom-0 bg-background border-t border-border p-6 -mx-6 -mb-6">
            <div className="flex items-center space-x-4">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1" disabled={isLoading}>Cancelar</Button>
              <Button type="submit" loading={isLoading} iconName="Save" iconPosition="left" className="flex-1">Salvar Alterações</Button>
            </div>
          </div>
        </form>
      </motion.div>
    </>
  );
};

export default EditFilamentDrawer;